'use client';

import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { apiClient } from '@/lib/api-client';
import { buildCodebookTreeFromFlatFiles, filterCodebookTree, isCodebookFileName } from '@/lib/codebook-tree-utils';
import { decideCodebookTreeFetch, isSharePointPilotUser } from '@/lib/codebook-tree-fetch-guard';
import { useSharePointAuth } from '@/hooks/use-sharepoint-auth';
import { errorMessage } from '@/lib/errors';
import type { CodebookForm, CodebookSchema, CodebookType } from '@/types/codebook-schema';
import { fingerprintLayer1, fingerprintLayer2, fingerprintLayer3, isLayerDirty, isPlanDirty } from '@/lib/test-cases/plan-dirty';
import { codebookParseFlightKey, isValLayerFormOid, mergeSuiteRunsByFormOid, shouldApplyParsedSchema, shouldSkipDuplicateParse } from '@/lib/test-cases/codebook-parse-flight';
const SHAREPOINT_DELEGATED_AUTH_PILOT_EMAIL = process.env.NEXT_PUBLIC_SHAREPOINT_DELEGATED_AUTH_PILOT_EMAIL?.toLowerCase();

// ── Types ──────────────────────────────────────────────────────────────

export interface TestStep {
  step: number;
  action: string;
  field: string;
  input: string;
  expected: string;
  flag_yellow?: boolean;
  ncdr_rule?: string;
}
export interface TestCase {
  id: string;
  title: string;
  steps: TestStep[];
  raw_text?: string;
  // Codebook metadata (filterable in UI)
  displayed_in_ui?: string | null;
  font_color_name?: string | null;
  data_type?: string | null;
  form_name?: string | null;
}
export interface TestCategory {
  name: string;
  test_cases: TestCase[];
}
export interface TestCaseResult {
  categories: TestCategory[];
}

// ── STRIDE form schema ─────────────────────────────────────────────────
// Shapes below mirror what the backend actually builds in
// `test_case_generator.get_form_schema` — one row per ODM ItemDef, grouped
// into ItemGroups, with the CodeLists and ConditionDefs those items reference.

export interface StrideItem {
  oid: string;
  name: string;
  data_type: string | null;
  /** ODM ItemDef.Length, passed straight through from Postgres. */
  length: number | string | null;
  question: string | null;
  /** ODM ItemRef.Mandatory — the string "Yes"/"No", not a boolean. */
  mandatory: string | null;
  condition_oid: string | null;
  /** Present only when the item references a codelist. */
  codelist_oid?: string;
}
export interface StrideCodelistOption {
  coded_value: string;
  decode: string | null;
}
export interface StrideCodelist {
  oid: string;
  name: string;
  items: StrideCodelistOption[];
}
export interface StrideCondition {
  oid: string;
  name: string;
  context: string | null;
  expression: string | null;
}
export interface StrideItemGroup {
  oid: string;
  name: string;
  item_oids: string[];
}
export interface FormSchema {
  study_oid: string;
  form_oid: string;
  items: StrideItem[];
  codelists: StrideCodelist[];
  conditions: StrideCondition[];
  item_groups: StrideItemGroup[];
  summary: {
    item_count: number;
    codelist_count: number;
    condition_count: number;
    group_count: number;
  };
}

// ── Parsed codebook ────────────────────────────────────────────────────
// One declaration, in `@/types/codebook-schema`, derived from
// `codebook_parser._parse_row` / `_attach_multiselect_children` /
// `_build_schema`. Re-exported here because consumers already import these
// names from the context.
export type { CodebookItem, CodebookMultiselectChild, CodebookForm, CodebookSchemaSummary, CodebookSchema } from '@/types/codebook-schema';

/** One highlighted cell from a codebook version diff
 *  (`test_case_generator.parse_codebook`). */
export interface CodebookChange {
  sheet: string;
  row: number;
  column: string;
  value: string;
  color: string;
}
export type GenerationPhase = 'idle' | 'schema' | 'prompt' | 'generating' | 'parsing' | 'formatting';
export type TestCaseMode = 'codebook' | 'stride';
export type StrideLayer = 'layer1' | 'layer2' | 'layer3';
export interface ScenarioResult {
  id: string;
  title: string;
  condition_oid?: string;
  steps: TestStep[];
  type: 'conditional_cascade' | 'state_transition' | 'happy_path' | 'regression';
  /** NCDR registry elements this scenario exercises. Absent on non-NCDR studies. */
  ncdr_elements?: string[];
  /** Free-text description of the patient this scenario is written around. */
  patient_profile?: string;
}
export interface ScenarioReview {
  status: 'unreviewed' | 'approved' | 'changes_requested';
  note?: string;
}

/** A Layer-2 compliance gap. The generator returns these as a *sibling* of
 *  `scenarios` in the result payload — not as a field on each scenario — and
 *  Layer 3's prompt reads them from that same top level
 *  (`qa_agent._build_scenarios_prompt` / `_build_gaps_prompt`). Model-produced,
 *  so every field is treated as optional even though all five are asked for. */
export interface ComplianceGap {
  ncdr_element?: string;
  element_name?: string;
  coding_instruction?: string;
  gap_description?: string;
  severity?: 'HIGH' | 'MEDIUM' | 'LOW';
}

/** One entry of `unmapped_ncdr_elements` in the Layer-3 result. Model-produced,
 *  so every field is optional (`qa_agent._build_gaps_prompt` output schema). */
export interface UnmappedNcdrElement {
  element_number?: string | number;
  element_name?: string;
  section?: string;
  coding_instruction?: string;
  severity?: GapResult['severity'];
  remediation?: string;
}
export type GapDisposition = 'open' | 'applied' | 'dismissed' | 'confirmed';
export interface GapResult {
  gap_id: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  gap_type: string;
  remediation?: string;
  confirmed_by_layer2?: boolean;
  applied?: boolean;
  // NCDR-specific fields. The UI has always read these — through casts,
  // because the interface stopped short of what the generator sends.
  /** Registry element number, e.g. 4500. */
  ncdr_element_number?: string | number;
  ncdr_element_name?: string;
  /** Supporting quote from the source document. */
  evidence?: string;
  /** The registry's own instruction for coding this element. */
  coding_instruction?: string;
}

/** The `result_json` this provider writes for a `val:scenarios:*` run — see
 *  saveScenarioSuite below. Every field is optional because older saved runs
 *  predate the newer ones. */
interface SavedScenarioSuite {
  scenarios?: ScenarioResult[];
  reviews?: Record<string, ScenarioReview>;
  compliance_gaps?: ComplianceGap[];
  approved_count?: number;
  saved_at?: string;
}

/** The `result_json` this provider writes for a `val:gaps:*` run — see
 *  saveGapSuite below. */
interface SavedGapSuite {
  gaps?: GapResult[];
  dispositions?: Record<string, GapDisposition>;
  summary?: {
    total: number;
    applied: number;
    confirmed: number;
    dismissed: number;
  };
  saved_at?: string;
}
interface Study {
  OID: string;
  StudyName: string;
}
interface Form {
  form_oid: string;
  form_name: string;
}

// ── Context value ──────────────────────────────────────────────────────

export interface SuiteRun {
  id: string;
  form_oid: string;
  form_name: string;
  item_group_oids: string[] | null;
  updated_at: string;
}
export interface SuiteInfo {
  id: string;
  study_oid: string;
  study_name: string;
  created_at: string;
}

// ── Codebook browser types ──────────────────────────────────────────────────

export interface TreeNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  path: string;
  last_modified: string;
  size?: number;
  children: TreeNode[];
}

/** @deprecated Use TreeNode instead */
export interface CodebookFile {
  id: string;
  name: string;
  size: number;
  last_modified: string;
}
interface TestCaseContextValue {
  mode: TestCaseMode;
  // Codebook tree browser (primary input)
  codebookTree: TreeNode[];
  codebookSource: string;
  codebookFiles: CodebookFile[]; // deprecated — kept for compat
  selectedCodebookFile: string;
  selectedCodebookPath: string;
  codebookSheets: string[];
  selectedCodebookSheet: string;
  /** L3PHC (default) vs Anvil Data Type map for parse + L1 generate. */
  codebookType: CodebookType;
  setCodebookType: (type: CodebookType) => void;
  codebookSchema: CodebookSchema | null;
  codebookForms: CodebookForm[];
  selectedCodebookForms: string[];
  loadingCodebookTree: boolean;
  loadingCodebookSheets: boolean;
  loadingCodebookSchema: boolean;
  selectCodebookFile: (fileId: string, source?: string, fileName?: string, filePath?: string) => void;
  refreshCodebookTree: () => void;
  selectCodebookSheet: (sheet: string) => void;
  toggleCodebookForm: (formName: string) => void;
  generateManual: () => Promise<void>;
  downloadADO: () => Promise<void>;
  importXlsx: (file: File) => Promise<void>;
  clearSession: () => Promise<void>;
  /** Wipe L1/L2/L3 work; keep codebook + STRIDE setup. Does not delete suite runs. */
  clearWork: () => void;
  hasPlanWork: boolean;
  isPlanComplete: boolean;
  isDirty: boolean;
  /** If dirty, opens the leave guard; otherwise clearWork(). */
  requestNewTestPlan: () => void;
  newPlanGuardOpen: boolean;
  setNewPlanGuardOpen: (open: boolean) => void;
  /** Persist every dirty layer (leave-path, relaxed review gates), then clearWork. */
  savePlanForLeave: () => Promise<void>;
  savingPlanForLeave: boolean;
  retryFetchStudies?: () => void;

  // Data
  studies: Study[];
  forms: Form[];
  selectedStudy: string;
  selectedForm: string;
  schema: FormSchema | null;
  selectedGroups: string[];
  codebookDelta: CodebookChange[] | null;
  codebookFilename: string;
  result: TestCaseResult | null;
  customInstructions: string;
  edits: Record<string, string>;

  // Suite / persistence
  suite: SuiteInfo | null;
  suiteRuns: SuiteRun[];
  saving: boolean;

  // Actions
  selectStudy: (studyOid: string) => void;
  selectForm: (formOid: string) => void;
  toggleGroup: (oid: string) => void;
  clearGroups: () => void;
  uploadCodebook: (file: File) => Promise<void>;
  generate: () => Promise<void>;
  downloadExcel: () => Promise<void>;
  saveRun: () => Promise<boolean>;
  updateCustomInstructions: (value: string) => void;
  getEditedValue: (cat: string, tcId: string, step: number, field: string, original: string) => string;
  setEditedValue: (cat: string, tcId: string, step: number, field: string, value: string) => void;
  addTestCase: (categoryName: string) => void;
  deleteTestCases: (categoryName: string, tcIds: string[]) => void;
  addStepToTestCase: (categoryName: string, tcId: string) => void;
  deleteStepFromTestCase: (categoryName: string, tcId: string, stepIndex: number) => void;
  clearCodebook: () => void;

  // Loading states
  loadingStudies: boolean;
  loadingForms: boolean;
  loadingSchema: boolean;
  generating: boolean;
  generationPhase: GenerationPhase;
  isImporting: boolean;
  parsingCodebook: boolean;
  error: string | null;

  // Val pipeline — layer navigation
  activeLayer: StrideLayer;
  setActiveLayer: (layer: StrideLayer) => void;
  // Layer 2 — Scenario tests
  scenarioResults: ScenarioResult[] | null;
  generatingScenarios: boolean;
  scenarioProgress: {
    message: string;
    step: number;
    total: number;
    n_mappings?: number;
    n_ncdr?: number;
    max_tokens?: number;
    item_count?: number;
    prompt_chars?: number;
  } | null;
  scenarioStats: {
    input_tokens: number;
    output_tokens: number;
  } | null;
  scenarioTokenCount: number;
  scenarioStreamText: string;
  generateScenarios: () => Promise<void>;
  fetchStrideFormsOnly: (studyOid: string) => Promise<void>;
  scenarioReviews: Record<string, ScenarioReview>;
  setScenarioReview: (id: string, review: ScenarioReview) => void;
  savingScenarios: boolean;
  saveScenarioSuite: () => Promise<boolean>;
  // Layer 3 — Gap analysis
  gapResults: GapResult[] | null;
  generatingGaps: boolean;
  gapProgress: {
    message: string;
    step: number;
    total: number;
    n_mappings?: number;
    n_ncdr?: number;
    max_tokens?: number;
    item_count?: number;
    prompt_chars?: number;
  } | null;
  gapStats: {
    input_tokens: number;
    output_tokens: number;
  } | null;
  gapTokenCount: number;
  gapStreamText: string;
  generateGaps: () => Promise<void>;
  gapDispositions: Record<string, GapDisposition>;
  setGapDisposition: (id: string, d: GapDisposition) => void;
  savingGaps: boolean;
  saveGapSuite: () => Promise<boolean>;
  appliedGaps: Set<string>;
  applyGapFix: (gapId: string) => Promise<void>;
  // Val augmentation — matched study from STRIDE DB
  strideMatchedStudy: {
    oid: string;
    name: string;
  } | null;
  setStrideMatchedStudy: (study: {
    oid: string;
    name: string;
  } | null) => void;
}
const TestCaseContext = createContext<TestCaseContextValue | null>(null);

// ── Helpers ────────────────────────────────────────────────────────────

const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/** Stable empty list for "no codebook parsed yet". A fresh `[]` here would give
 *  the context value a new `codebookForms` identity on every render and defeat
 *  the useMemo that guards every consumer of this provider. */
const NO_CODEBOOK_FORMS: CodebookForm[] = [];

/** Extract a URL-safe study key from a codebook file path.
 *  "VITAL-EP/V3.0/file.xlsx" → "cb:VITAL-EP"
 *  "file.xlsx" (flat blob) → "cb:file"
 */
function codebookStudyKey(filePath: string): string {
  const first = filePath.split('/')[0].replace(/\.[^.]+$/, '');
  return `cb:${first}`;
}

/** Same key saveRun / loadSavedRun / CodebookProgress use — never mutate the source array. */
function codebookFormOid(forms: string[]): string {
  if (forms.length === 0) return 'all-forms';
  return [...forms].sort().join('+');
}

// ── Provider ───────────────────────────────────────────────────────────

export function TestCaseProvider({
  children
}: {
  children: ReactNode;
}) {
  // ── Codebook browser state ───────────────────────────────────────────────
  const [codebookTree, setCodebookTree] = useState<TreeNode[]>([]);
  const [codebookSource, setCodebookSource] = useState<string>('');
  const [loadingCodebookTree, setLoadingCodebookTree] = useState(false);
  const [codebookFiles, setCodebookFiles] = useState<CodebookFile[]>([]);
  const [selectedCodebookFile, setSelectedCodebookFile] = useState<string>('');
  // The tree node's folder path (e.g. "DISRUPT-AF/Codebook/file.xlsx"), separate
  // from selectedCodebookFile (the Graph item ID/blob key used to actually fetch
  // the file). SharePoint tree nodes use an opaque Graph ID with no path
  // structure, unlike blob keys which happen to look like paths — study-name
  // derivation (STRIDE auto-match, saved-suite grouping, export filenames) needs
  // this path, not the ID, or every SharePoint file version gets treated as a
  // different "study" (found live 2026-07-27, AB#20867 pilot).
  const [selectedCodebookPath, setSelectedCodebookPath] = useState<string>('');
  const [selectedCodebookSource, setSelectedCodebookSource] = useState<string>('');
  const [codebookSheets, setCodebookSheets] = useState<string[]>([]);
  const [selectedCodebookSheet, setSelectedCodebookSheet] = useState<string>('');
  const [codebookType, setCodebookTypeState] = useState<CodebookType>(() => {
    if (typeof window === 'undefined') return 'l3phc';
    const saved = localStorage.getItem('crucible:tc-codebook-type');
    return saved === 'anvil' ? 'anvil' : 'l3phc';
  });
  const codebookTypeRef = useRef(codebookType);
  codebookTypeRef.current = codebookType;
  const [codebookSchema, setCodebookSchema] = useState<CodebookSchema | null>(null);
  const [selectedCodebookForms, setSelectedCodebookForms] = useState<string[]>([]);
  const firstSelectedForm = selectedCodebookForms[0];
  const [loadingCodebookSheets, setLoadingCodebookSheets] = useState(false);
  const [loadingCodebookSchema, setLoadingCodebookSchema] = useState(false);
  const codebookForms = codebookSchema?.forms ?? NO_CODEBOOK_FORMS;

  // Data state
  const [studies, setStudies] = useState<Study[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [selectedStudy, setSelectedStudy] = useState<string>('');
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [codebookDelta, setCodebookDelta] = useState<CodebookChange[] | null>(null);
  const [codebookFilename, setCodebookFilename] = useState<string>('');
  const [result, setResult] = useState<TestCaseResult | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [customInstructions, setCustomInstructions] = useState<string>('');

  // Loading states
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('idle');
  const [isImporting, setIsImporting] = useState(false);
  const [parsingCodebook, setParsingCodebook] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suite / persistence
  const [suite, setSuite] = useState<SuiteInfo | null>(null);
  const [suiteRuns, setSuiteRuns] = useState<SuiteRun[]>([]);
  const [saving, setSaving] = useState(false);

  // Ship/leave — fingerprints of last successful save per layer
  const [savedL1Fingerprint, setSavedL1Fingerprint] = useState<string | null>(null);
  const [savedL2Fingerprint, setSavedL2Fingerprint] = useState<string | null>(null);
  const [savedL3Fingerprint, setSavedL3Fingerprint] = useState<string | null>(null);
  const [newPlanGuardOpen, setNewPlanGuardOpen] = useState(false);
  const [savingPlanForLeave, setSavingPlanForLeave] = useState(false);

  // ── Val pipeline state ──────────────────────────────────────────────────────
  const [activeLayer, setActiveLayer] = useState<StrideLayer>('layer1');
  const [scenarioResults, setScenarioResults] = useState<ScenarioResult[] | null>(null);
  const [generatingScenarios, setGeneratingScenarios] = useState(false);
  const [scenarioProgress, setScenarioProgress] = useState<{
    message: string;
    step: number;
    total: number;
    n_mappings?: number;
    n_ncdr?: number;
    max_tokens?: number;
    item_count?: number;
    prompt_chars?: number;
  } | null>(null);
  const [scenarioStats, setScenarioStats] = useState<{
    input_tokens: number;
    output_tokens: number;
  } | null>(null);
  const [scenarioTokenCount, setScenarioTokenCount] = useState(0);
  const scenarioTokenRef = useRef(0);
  const [scenarioStreamText, setScenarioStreamText] = useState('');
  const scenarioStreamRef = useRef('');
  const [scenarioReviews, setScenarioReviews] = useState<Record<string, ScenarioReview>>({});
  // Layer 2 reports its compliance gaps alongside `scenarios`, not inside them.
  // Layer 3's prompt reads them from `layer2_results.compliance_gaps`, so they
  // have to survive the trip between the two calls.
  const [scenarioComplianceGaps, setScenarioComplianceGaps] = useState<ComplianceGap[]>([]);
  const [savingScenarios, setSavingScenarios] = useState(false);
  const [gapResults, setGapResults] = useState<GapResult[] | null>(null);
  const [generatingGaps, setGeneratingGaps] = useState(false);
  const [gapProgress, setGapProgress] = useState<{
    message: string;
    step: number;
    total: number;
    n_mappings?: number;
    n_ncdr?: number;
    max_tokens?: number;
    item_count?: number;
    prompt_chars?: number;
  } | null>(null);
  const [gapStats, setGapStats] = useState<{
    input_tokens: number;
    output_tokens: number;
  } | null>(null);
  const [gapTokenCount, setGapTokenCount] = useState(0);
  const gapTokenRef = useRef(0);
  const [gapStreamText, setGapStreamText] = useState('');
  const gapStreamRef = useRef('');
  const [gapDispositions, setGapDispositions] = useState<Record<string, GapDisposition>>({});
  const [savingGaps, setSavingGaps] = useState(false);
  const [appliedGaps, setAppliedGaps] = useState<Set<string>>(new Set());
  const [strideMatchedStudy, setStrideMatchedStudy] = useState<{
    oid: string;
    name: string;
  } | null>(null);
  // Named locals for the two values the STRIDE effects key on. A member
  // expression in a dependency array cannot be checked statically, so the
  // rule could not tell whether those arrays were complete.
  const strideOid = strideMatchedStudy?.oid;

  // Track whether studies have been fetched to avoid re-fetching on navigation
  const [studiesFetched, setStudiesFetched] = useState(false);
  // Track whether we've attempted session restore (runs once)
  const [restored, setRestored] = useState(false);

  // Invalidates in-flight codebook async work when the user changes selection
  const codebookEpochRef = useRef(0);
  const selectedCodebookFileRef = useRef(selectedCodebookFile);
  const selectedCodebookSourceRef = useRef(selectedCodebookSource);
  const selectedCodebookSheetRef = useRef(selectedCodebookSheet);
  const codebookSchemaRef = useRef(codebookSchema);
  /** Blocks repeat orphan-reparses for the same file|sheet after a failed/racy hydrate. */
  const orphanParseKeyRef = useRef<string | null>(null);
  /** Single-flight key so hydrate + orphan don't stampede SharePoint. */
  const inFlightParseKeyRef = useRef<string | null>(null);
  useEffect(() => {
    selectedCodebookFileRef.current = selectedCodebookFile;
    selectedCodebookSourceRef.current = selectedCodebookSource;
  }, [selectedCodebookFile, selectedCodebookSource]);
  useEffect(() => {
    selectedCodebookSheetRef.current = selectedCodebookSheet;
  }, [selectedCodebookSheet]);
  useEffect(() => {
    codebookSchemaRef.current = codebookSchema;
  }, [codebookSchema]);
  const bumpCodebookEpoch = useCallback(() => {
    codebookEpochRef.current += 1;
    // Drop spinners owned by the superseded request. Without this, a cancelled
    // hydrate can leave loadingCodebookSchema=true forever and the form picker
    // never leaves “Loading forms…”. The new owner sets loading again synchronously.
    setLoadingCodebookSheets(false);
    setLoadingCodebookSchema(false);
    return codebookEpochRef.current;
  }, []);

  // ── localStorage keys for session persistence ───────────────────────
  const LS_STUDY = 'crucible:tc-selected-study';
  const LS_FORM = 'crucible:tc-selected-form';
  const LS_CB_FILE = 'crucible:tc-codebook-file';
  const LS_CB_PATH = 'crucible:tc-codebook-path';
  const LS_CB_SOURCE = 'crucible:tc-codebook-source';
  const LS_CB_SHEET = 'crucible:tc-codebook-sheet';
  const LS_CB_FORMS = 'crucible:tc-codebook-forms';
  const LS_CB_TYPE = 'crucible:tc-codebook-type';

  // Persist selections to localStorage whenever they change
  useEffect(() => {
    if (!restored) return; // don't write until restore has run
    if (selectedStudy) localStorage.setItem(LS_STUDY, selectedStudy);else localStorage.removeItem(LS_STUDY);
  }, [selectedStudy, restored]);
  useEffect(() => {
    if (!restored) return;
    if (selectedForm) localStorage.setItem(LS_FORM, selectedForm);else localStorage.removeItem(LS_FORM);
  }, [selectedForm, restored]);

  // Persist codebook selections
  useEffect(() => {
    if (!restored) return;
    if (selectedCodebookFile) {
      localStorage.setItem(LS_CB_FILE, selectedCodebookFile);
      localStorage.setItem(LS_CB_PATH, selectedCodebookPath || selectedCodebookFile);
      localStorage.setItem(LS_CB_SOURCE, selectedCodebookSource || codebookSource);
    } else {
      localStorage.removeItem(LS_CB_FILE);
      localStorage.removeItem(LS_CB_PATH);
      localStorage.removeItem(LS_CB_SOURCE);
      localStorage.removeItem(LS_CB_SHEET);
    }
  }, [selectedCodebookFile, selectedCodebookPath, selectedCodebookSource, codebookSource, restored]);
  useEffect(() => {
    if (!restored) return;
    if (selectedCodebookSheet) localStorage.setItem(LS_CB_SHEET, selectedCodebookSheet);else localStorage.removeItem(LS_CB_SHEET);
  }, [selectedCodebookSheet, restored]);
  useEffect(() => {
    if (!restored) return;
    localStorage.setItem(LS_CB_TYPE, codebookType);
  }, [codebookType, restored]);
  useEffect(() => {
    if (!restored) return;
    if (selectedCodebookForms.length > 0) {
      localStorage.setItem(LS_CB_FORMS, JSON.stringify(selectedCodebookForms));
    } else {
      localStorage.removeItem(LS_CB_FORMS);
    }
  }, [selectedCodebookForms, restored]);

  // DEV: load mock data when ?mock=true is in the URL (avoids waiting for AI generation)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('mock') !== 'true') return;
    setResult({
      categories: [{
        name: 'Happy Path',
        test_cases: [{
          id: 'HP-001',
          title: 'Basic demographics entry',
          steps: [{
            step: 1,
            action: 'Navigate to Demographics form and open the first visit',
            field: 'FORM_DM',
            input: 'Click CRF link',
            expected: 'Form opens with all fields blank and editable'
          }, {
            step: 2,
            action: 'Enter subject date of birth in the date field',
            field: 'DM.BRTHDTC',
            input: '1985-06-15',
            expected: 'Date accepted, age auto-calculated to 41'
          }, {
            step: 3,
            action: 'Select gender from the codelist dropdown',
            field: 'DM.SEX',
            input: 'Female',
            expected: 'Value "F" stored, field highlighted green'
          }]
        }, {
          id: 'HP-002',
          title: 'Vital signs entry',
          steps: [{
            step: 1,
            action: 'Open Vital Signs form for Visit 2',
            field: 'FORM_VS',
            input: 'Click CRF link',
            expected: 'Form opens showing previous visit data in read-only'
          }, {
            step: 2,
            action: 'Enter systolic blood pressure',
            field: 'VS.SYSBP',
            input: '120',
            expected: 'Value accepted, within normal range indicator shown'
          }, {
            step: 3,
            action: 'Enter diastolic blood pressure',
            field: 'VS.DIABP',
            input: '80',
            expected: 'Value accepted, no range warning triggered'
          }, {
            step: 4,
            action: 'Record pulse rate value',
            field: 'VS.PULSE',
            input: '72',
            expected: 'Accepted, normal range (60-100 bpm)'
          }]
        }]
      }, {
        name: 'Boundary/Range',
        test_cases: [{
          id: 'BR-001',
          title: 'Age boundary validation',
          steps: [{
            step: 1,
            action: 'Enter minimum allowed age (inclusion criterion ≥18)',
            field: 'DM.AGE',
            input: '17',
            expected: 'Range check fires: "Subject must be ≥18 years"'
          }, {
            step: 2,
            action: 'Enter exactly the minimum boundary age',
            field: 'DM.AGE',
            input: '18',
            expected: 'Accepted with no warning'
          }, {
            step: 3,
            action: 'Enter maximum allowed age (inclusion criterion ≤85)',
            field: 'DM.AGE',
            input: '86',
            expected: 'Range check fires: "Subject must be ≤85 years"'
          }]
        }]
      }, {
        name: 'Negative',
        test_cases: [{
          id: 'NEG-001',
          title: 'Invalid data type entry',
          steps: [{
            step: 1,
            action: 'Enter alphabetic text in a numeric-only field',
            field: 'VS.SYSBP',
            input: 'abc',
            expected: 'Field rejects input or shows "Must be numeric" error'
          }, {
            step: 2,
            action: 'Enter special characters in subject initials field',
            field: 'DM.SUBJID',
            input: '<script>alert(1)</script>',
            expected: 'Input sanitized, special characters stripped'
          }]
        }]
      }]
    });
    setSelectedStudy('MOCK-001');
    setSelectedForm('MOCK-FORM');
    setRestored(true);
  }, []);

  // Load/save custom instructions from localStorage
  const instructionsKey = selectedStudy && selectedForm ? `crucible:tc-instructions:${selectedStudy}:${selectedForm}` : null;
  useEffect(() => {
    if (instructionsKey) {
      const saved_0 = localStorage.getItem(instructionsKey);
      if (saved_0) setCustomInstructions(saved_0);else setCustomInstructions('');
    }
  }, [instructionsKey]);
  const updateCustomInstructions = useCallback((value: string) => {
    setCustomInstructions(value);
    if (instructionsKey) {
      if (value.trim()) localStorage.setItem(instructionsKey, value);else localStorage.removeItem(instructionsKey);
    }
  }, [instructionsKey]);

  // ── Codebook browser actions ─────────────────────────────────────────────

  const {
    data: session
  } = useSession();
  const {
    acquireSharePointToken
  } = useSharePointAuth();

  // Every codebook-browser call (tree, sheets, download) needs the same
  // pilot-gated token — the tree fetch and the file-pick actions are
  // separate requests, so each one re-derives it rather than sharing a
  // single stale value across the session.
  //
  // Gated on session.id_token, not just session.user.email: email resolves
  // in the NextAuth session before id_token does (id_token requires an
  // extra server-side token-store round-trip in auth.ts's session()
  // callback), and use-workspace-handlers.ts's apiClient.setToken() call is
  // keyed on that same id_token. Without id_token in this dependency list,
  // a "codebook-tree" fetch that races ahead of it locks in isPilotUser
  // from a stale, already-settled session reference — email never changes
  // again, so the effect below never retries and the browser is stuck on
  // the app-only/blob fallback until a fresh reload happens to win the
  // race (found live 2026-07-27, AB#20867 pilot).
  const sessionIdToken = session?.id_token;
  const getSharePointToken = useCallback(async (): Promise<string | undefined> => {
    const isPilotUser = !!sessionIdToken && isSharePointPilotUser(SHAREPOINT_DELEGATED_AUTH_PILOT_EMAIL, session?.user?.email);
    const spToken = isPilotUser ? await acquireSharePointToken() : null;
    return spToken ?? undefined;
  }, [session?.user?.email, sessionIdToken, acquireSharePointToken]);

  // The session-restore effect below must run exactly once, but it needs a
  // token getter that is current at the moment it awaits one — getSharePointToken
  // changes identity as the session (and the cached SharePoint token) settles.
  // Depending on it directly would restart a restore already in flight, so the
  // effect reads the latest getter through this ref instead.
  const getSharePointTokenRef = useRef(getSharePointToken);
  useEffect(() => {
    getSharePointTokenRef.current = getSharePointToken;
  }, [getSharePointToken]);
  const fetchCodebookTree = useCallback(async (refresh = false) => {
    setLoadingCodebookTree(true);
    try {
      // Acquire first, then decide — pilots must not call /codebook-tree without
      // a delegated token (app-only/blob fallback locks opaque folder ids in UI).
      const spToken_0 = await getSharePointToken();
      const decision = decideCodebookTreeFetch({
        pilotEmail: SHAREPOINT_DELEGATED_AUTH_PILOT_EMAIL,
        userEmail: session?.user?.email,
        sessionIdToken,
        sharePointToken: spToken_0
      });
      if (decision.action === 'defer') {
        console.warn(`[codebook-tree] deferring fetch (${decision.reason}) — waiting for SharePoint auth`);
        return;
      }
      const data = refresh ? await apiClient.refreshCodebookTree(spToken_0) : await apiClient.listCodebookTree(spToken_0);
      let tree = data.tree ?? [];
      let source = data.source ?? '';
      if (tree.length === 0) {
        const legacy_0 = await apiClient.listCodebookFiles();
        setCodebookFiles(legacy_0.files);
        tree = buildCodebookTreeFromFlatFiles(legacy_0.files);
        source = 'blob';
      }
      setCodebookTree(filterCodebookTree(tree));
      setCodebookSource(source);
    } catch (e) {
      // Fallback to legacy flat listing when Graph/tree endpoint is unavailable
      try {
        const legacy = await apiClient.listCodebookFiles();
        setCodebookFiles(legacy.files);
        setCodebookTree(filterCodebookTree(buildCodebookTreeFromFlatFiles(legacy.files)));
        setCodebookSource('blob');
      } catch {
        setError(errorMessage(e));
      }
    } finally {
      setLoadingCodebookTree(false);
    }
  }, [getSharePointToken, session?.user?.email, sessionIdToken]);
  const refreshCodebookTree = useCallback(() => {
    fetchCodebookTree(true);
  }, [fetchCodebookTree]);
  const formatCodebookError = (message: string) => {
    if (message.includes('ChainedTokenCredential') || message.includes('claims-challenge')) {
      return 'SharePoint needs re-authentication. A browser sign-in may open automatically — if not, run `az login` and retry.';
    }
    return message.split('\n')[0].slice(0, 240);
  };
  const applyParsedSchema = useCallback((schema_0: CodebookSchema, epoch: number) => {
    const epochOk = codebookEpochRef.current === epoch;
    // Prefer epoch match; if a restore race superseded us, still accept the
    // result when the user hasn't moved off this sheet (keeps the form picker alive).
    const sheetOk = !schema_0.sheet || !selectedCodebookSheetRef.current || schema_0.sheet === selectedCodebookSheetRef.current;
    if (!shouldApplyParsedSchema(epochOk, sheetOk)) return;
    setCodebookSchema(schema_0);
    if (schema_0.sheets_available?.length) {
      setCodebookSheets(schema_0.sheets_available);
    }
    if (schema_0.sheet) {
      setSelectedCodebookSheet(schema_0.sheet);
    }
    setError(null);
    setLoadingCodebookSchema(false);
  }, []);
  const parseCodebookSheet = useCallback(async (fileId: string, sheet: string | undefined, src: string | undefined, epoch_0: number) => {
    // Capture once — avoid a mid-flight render sync resetting the profile.
    const ctype = codebookTypeRef.current;
    const flightKey = codebookParseFlightKey(fileId, sheet, ctype);
    if (inFlightParseKeyRef.current === flightKey) return;
    inFlightParseKeyRef.current = flightKey;
    setLoadingCodebookSchema(true);
    try {
      const spToken_1 = await getSharePointToken();
      const schema_1 = await apiClient.parseCodebookFromBlob(fileId, sheet || undefined, src, spToken_1, ctype);
      applyParsedSchema(schema_1, epoch_0);
    } catch (e_0) {
      // A twin request (Strict Mode / orphan) may already have filled the picker —
      // don't overwrite a good schema with a transient Graph 502/503.
      const alreadyHaveForms = (codebookSchemaRef.current?.forms?.length ?? 0) > 0;
      const stillThisSheet = !sheet || sheet === selectedCodebookSheetRef.current;
      if (!alreadyHaveForms && stillThisSheet && (codebookEpochRef.current === epoch_0 || sheet === selectedCodebookSheetRef.current)) {
        setError(formatCodebookError(errorMessage(e_0)));
      }
    } finally {
      if (inFlightParseKeyRef.current === flightKey) inFlightParseKeyRef.current = null;
      // Always release the spinner for this attempt — epoch-gated clears left
      // “Loading forms…” stuck when a superseded parse finished after bump.
      setLoadingCodebookSchema(false);
    }
  }, [applyParsedSchema, getSharePointToken]);
  const selectCodebookFile = useCallback(async (fileId_0: string, source_0?: string, fileName?: string, filePath?: string) => {
    const epoch_1 = bumpCodebookEpoch();
    setError(null);
    const label = fileName ?? fileId_0.split('/').pop() ?? fileId_0;
    if (fileId_0 && !isCodebookFileName(label)) {
      setSelectedCodebookFile('');
      setSelectedCodebookPath('');
      setSelectedCodebookSource('');
      setCodebookSheets([]);
      setCodebookSchema(null);
      setSelectedCodebookForms([]);
      setLoadingCodebookSheets(false);
      setLoadingCodebookSchema(false);
      setError('Select an Excel or CSV codebook (.xlsx, .xls, .xlsm, .csv) — not README or other docs.');
      return;
    }
    setSelectedCodebookFile(fileId_0);
    setSelectedCodebookPath(filePath || fileId_0);
    setSelectedCodebookSource(source_0 || codebookSource || '');
    setSelectedCodebookSheet('');
    setCodebookSheets([]);
    setCodebookSchema(null);
    setSelectedCodebookForms([]);
    // Clear STRIDE session so it doesn't restore on refresh when codebook is active
    if (fileId_0) {
      localStorage.removeItem(LS_STUDY);
      localStorage.removeItem(LS_FORM);
    }
    if (!fileId_0) return;
    const src_0 = source_0 || codebookSource || undefined;

    // CSV files have no sheets — go straight to schema
    if (fileId_0.toLowerCase().endsWith('.csv')) {
      setLoadingCodebookSchema(true);
      try {
        const spToken_2 = await getSharePointToken();
        const schema_2 = await apiClient.parseCodebookFromBlob(fileId_0, undefined, src_0, spToken_2, codebookTypeRef.current);
        applyParsedSchema(schema_2, epoch_1);
      } catch (e_1) {
        if (codebookEpochRef.current === epoch_1) setError(formatCodebookError(errorMessage(e_1)));
      } finally {
        if (codebookEpochRef.current === epoch_1) setLoadingCodebookSchema(false);
      }
      return;
    }
    setLoadingCodebookSheets(true);
    try {
      const spToken_3 = await getSharePointToken();
      const data_0 = await apiClient.getCodebookSheets(fileId_0, src_0, spToken_3);
      if (codebookEpochRef.current !== epoch_1) return;
      if (data_0.sheets.length === 0) {
        await parseCodebookSheet(fileId_0, undefined, src_0, epoch_1);
        return;
      }
      setCodebookSheets(data_0.sheets);
      if (data_0.sheets.length === 1) {
        const only = data_0.sheets[0];
        setSelectedCodebookSheet(only);
        await parseCodebookSheet(fileId_0, only, src_0, epoch_1);
      }
    } catch (e_2) {
      if (codebookEpochRef.current !== epoch_1) return;
      try {
        await parseCodebookSheet(fileId_0, undefined, src_0, epoch_1);
      } catch {
        if (codebookEpochRef.current === epoch_1) {
          setError(formatCodebookError(errorMessage(e_2, 'Could not load codebook versions')));
        }
      }
    } finally {
      if (codebookEpochRef.current === epoch_1) setLoadingCodebookSheets(false);
    }
  }, [bumpCodebookEpoch, codebookSource, parseCodebookSheet, applyParsedSchema, getSharePointToken]);
  const selectCodebookSheet = useCallback(async (sheet_0: string) => {
    const epoch_2 = bumpCodebookEpoch();
    setError(null);
    setSelectedCodebookSheet(sheet_0);
    setCodebookSchema(null);
    setSelectedCodebookForms([]);
    const fileId_1 = selectedCodebookFileRef.current;
    if (!fileId_1 || !sheet_0) return;
    const src_1 = selectedCodebookSourceRef.current || undefined;
    await parseCodebookSheet(fileId_1, sheet_0, src_1, epoch_2);
  }, [bumpCodebookEpoch, parseCodebookSheet]);

  // Changing profile with a version (or CSV) selected re-parses under the new map.
  const setCodebookType = useCallback((type: CodebookType) => {
    if (type === codebookTypeRef.current) return;
    codebookTypeRef.current = type;
    setCodebookTypeState(type);
    const fileId_2 = selectedCodebookFileRef.current;
    if (!fileId_2) return;
    const isCsv = fileId_2.toLowerCase().endsWith('.csv');
    const sheet_1 = selectedCodebookSheetRef.current;
    if (!isCsv && !sheet_1) return;
    const epoch_3 = bumpCodebookEpoch();
    setError(null);
    setCodebookSchema(null);
    setSelectedCodebookForms([]);
    const src_2 = selectedCodebookSourceRef.current || undefined;
    void parseCodebookSheet(fileId_2, isCsv ? undefined : sheet_1, src_2, epoch_3);
  }, [bumpCodebookEpoch, parseCodebookSheet]);

  // Restore can leave file+sheet selected while a superseded hydrate drops the
  // parsed schema (Strict Mode / HMR epoch races). Re-parse once so FORMS appears.
  // Do NOT call selectCodebookSheet here — that nulls schema and bumps epoch,
  // which races the in-flight hydrate parse that often succeeds.
  useEffect(() => {
    if (!restored) return;
    if (!selectedCodebookFile) return;
    if (loadingCodebookSheets || loadingCodebookSchema) return;
    if ((codebookSchema?.forms?.length ?? 0) > 0) {
      orphanParseKeyRef.current = null;
      return;
    }
    const isCsv_0 = selectedCodebookFile.toLowerCase().endsWith('.csv');
    if (!isCsv_0 && !selectedCodebookSheet) return;
    const key = codebookParseFlightKey(selectedCodebookFile, isCsv_0 ? undefined : selectedCodebookSheet, codebookType);
    if (shouldSkipDuplicateParse(key, orphanParseKeyRef.current, inFlightParseKeyRef.current)) {
      return;
    }
    orphanParseKeyRef.current = key;
    const epoch_4 = codebookEpochRef.current;
    const src_3 = selectedCodebookSource || undefined;
    void parseCodebookSheet(selectedCodebookFile, isCsv_0 ? undefined : selectedCodebookSheet, src_3, epoch_4);
  }, [restored, selectedCodebookFile, selectedCodebookSheet, selectedCodebookSource, codebookType, codebookSchema, loadingCodebookSheets, loadingCodebookSchema, parseCodebookSheet]);

  // Single-select: clicking a form selects it exclusively (radio behaviour).
  // Switching form drops in-session L1–L3 so we don't show another form's plan;
  // the load-saved effect then rehydrates if a suite run exists for the new form.
  const toggleCodebookForm = useCallback((formName: string) => {
    setSelectedCodebookForms(prev => {
      const next = prev.length === 1 && prev[0] === formName ? [] : [formName];
      if (next[0] !== prev[0]) {
        setResult(null);
        setEdits({});
        setSelectedGroups([]);
        setScenarioResults(null);
        setScenarioReviews({});
        setScenarioComplianceGaps([]);
        setGapResults(null);
        setGapDispositions({});
        setAppliedGaps(new Set());
        setSavedL1Fingerprint(null);
        setSavedL2Fingerprint(null);
        setSavedL3Fingerprint(null);
        setActiveLayer('layer1');
      }
      return next;
    });
  }, []);
  const generateManual = useCallback(async () => {
    if (!codebookSchema) return;
    setGenerating(true);
    setError(null);
    setEdits({});
    setResult(null);
    // Clear downstream layers — they belong to the previous codebook context
    setScenarioResults(null);
    setScenarioReviews({});
    setScenarioComplianceGaps([]);
    setGapDispositions({});
    setGapResults(null);
    setAppliedGaps(new Set());
    setSavedL1Fingerprint(null);
    setSavedL2Fingerprint(null);
    setSavedL3Fingerprint(null);
    setActiveLayer('layer1');
    setGenerationPhase('generating');
    try {
      const formNames = selectedCodebookForms.length > 0 ? selectedCodebookForms : undefined;
      const data_1 = await apiClient.generateManualTestCases(codebookSchema, formNames, codebookType);
      setResult(data_1 as TestCaseResult);
      setGenerationPhase('formatting');
      await delay(300);
    } catch (e_3) {
      setError(errorMessage(e_3));
    } finally {
      setGenerating(false);
      setGenerationPhase('idle');
    }
  }, [codebookSchema, selectedCodebookForms, codebookType]);
  const downloadADO = useCallback(async () => {
    if (!result) return;
    try {
      const blob = await apiClient.exportADOExcel(result);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Build filename: StudyFolder_Form1+Form2_ADO_Test_Cases.xlsx
      const studyPart = selectedCodebookPath ? selectedCodebookPath.split('/')[0].replace(/[[\]()]/g, '').trim() : '';
      const formPart = selectedCodebookForms.length > 0 ? selectedCodebookForms[0].replace(/\s+/g, '_') : 'All_Forms';
      const filename = [studyPart, formPart, 'ADO_Test_Cases'].filter(Boolean).join('_') + '.xlsx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e_4) {
      setError(errorMessage(e_4));
    }
  }, [result, selectedCodebookPath, selectedCodebookForms]);
  const importXlsx = useCallback(async (file: File) => {
    // Pre-flight checks before touching the network
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Invalid file type. Please select an .xlsx or .xls file.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File is too large. Maximum size is 20 MB.');
      return;
    }
    if (file.size === 0) {
      setError('The selected file is empty.');
      return;
    }
    try {
      setIsImporting(true);
      setError(null);
      const imported: TestCaseResult = await apiClient.importXlsxTestPlan(file);
      setResult(imported);
      setEdits({});
      setSavedL1Fingerprint(null);
      const count = imported.categories?.reduce((s, c) => s + c.test_cases.length, 0) ?? 0;
      console.log(`[Val] Imported ${file.name}: ${count} test cases`);
    } catch (e_5) {
      const raw: string = errorMessage(e_5) || '';
      // Translate backend errors into user-friendly messages
      if (raw.includes('Missing required columns')) {
        setError(`This file doesn't look like a Val or ADO test plan export. ${raw}`);
      } else if (raw.includes('No test cases found')) {
        setError('No test cases were found in this file. Make sure it was exported from Val or ADO.');
      } else if (raw.includes('Cannot read') || raw.includes('openpyxl')) {
        setError('The file could not be read. It may be corrupted or password-protected.');
      } else {
        setError(raw || 'Failed to import the file. Please check it was exported from Val or ADO.');
      }
    } finally {
      setIsImporting(false);
    }
  }, []);

  /** Wipe L1/L2/L3 in-session work; keep codebook file/version/form + STRIDE. */
  const clearWork = useCallback(() => {
    setResult(null);
    setEdits({});
    setError(null);
    setSelectedGroups([]);
    setActiveLayer('layer1');
    setScenarioResults(null);
    setGapDispositions({});
    setScenarioReviews({});
    setScenarioComplianceGaps([]);
    setGapResults(null);
    setAppliedGaps(new Set());
    setScenarioProgress(null);
    setScenarioStats(null);
    setScenarioTokenCount(0);
    setScenarioStreamText('');
    setGapProgress(null);
    setGapStats(null);
    setGapTokenCount(0);
    setGapStreamText('');
    setSavedL1Fingerprint(null);
    setSavedL2Fingerprint(null);
    setSavedL3Fingerprint(null);
    setNewPlanGuardOpen(false);
    setGenerationPhase('idle');
  }, []);

  /** Hard wipe: codebook selection + work. Suite history is left intact. */
  const clearSession = useCallback(async () => {
    bumpCodebookEpoch();
    clearWork();
    setSelectedCodebookFile('');
    setSelectedCodebookPath('');
    setSelectedCodebookSource('');
    setSelectedCodebookSheet('');
    setCodebookSheets([]);
    setCodebookSchema(null);
    setSelectedCodebookForms([]);
    setSchema(null);
    setSelectedForm('');
    setCustomInstructions('');
    setCodebookDelta(null);
    setCodebookFilename('');
    setStrideMatchedStudy(null);
    setSuite(null);
    setSuiteRuns([]);
    localStorage.removeItem(LS_CB_FILE);
    localStorage.removeItem(LS_CB_PATH);
    localStorage.removeItem(LS_CB_SOURCE);
    localStorage.removeItem(LS_CB_SHEET);
    localStorage.removeItem(LS_CB_FORMS);
  }, [bumpCodebookEpoch, clearWork]);

  // Fetch codebook tree once the session can decide pilot vs app-only.
  // Pilot: wait for id_token here; fetchCodebookTree then acquires the
  // SharePoint token and refuses to call /codebook-tree without it
  // (decideCodebookTreeFetch). Non-pilot users load immediately (app-only / blob).
  useEffect(() => {
    const isPilot = isSharePointPilotUser(SHAREPOINT_DELEGATED_AUTH_PILOT_EMAIL, session?.user?.email);
    if (isPilot && !sessionIdToken) return;
    void fetchCodebookTree();
  }, [fetchCodebookTree, session?.user?.email, sessionIdToken]);

  // Fetch studies — only once, unless retried
  const fetchStudies = useCallback(async (force = false) => {
    if (studiesFetched && !force) return;
    setLoadingStudies(true);
    try {
      const data_2 = await apiClient.getTestCaseStudies();
      // STRIDE can return multiple rows sharing one OID; collapse to the first
      // so OID stays a stable unique key for <SelectItem> and .find() lookups.
      const seen = new Set<string>();
      const uniqueStudies = data_2.studies.filter(s_0 => {
        if (seen.has(s_0.OID)) return false;
        seen.add(s_0.OID);
        return true;
      });
      setStudies(uniqueStudies);
      setStudiesFetched(true);
    } catch (e_6) {
      // STRIDE studies are optional augmentation — don't block the UI with an error
      console.warn('STRIDE studies unavailable:', errorMessage(e_6));
      setStudies([]);
      setStudiesFetched(true);
    } finally {
      setLoadingStudies(false);
    }
  }, [studiesFetched]);
  const retryFetchStudies = useCallback(() => {
    setStudiesFetched(false);
    fetchStudies(true);
  }, [fetchStudies]);

  // Fetch forms for selected study
  const fetchForms = useCallback(async (studyOid: string) => {
    setLoadingForms(true);
    setError(null);
    setForms([]);
    setSelectedForm('');
    setSchema(null);
    setResult(null);
    setEdits({});
    try {
      const data_3 = await apiClient.getTestCaseForms(studyOid);
      // Deduplicate by form_oid (backend may return duplicates)
      const seen_0 = new Set<string>();
      const unique = data_3.forms.filter((f: Form) => {
        if (seen_0.has(f.form_oid)) return false;
        seen_0.add(f.form_oid);
        return true;
      });
      setForms(unique);
    } catch (e_7) {
      setError(errorMessage(e_7));
    } finally {
      setLoadingForms(false);
    }
  }, []);

  // Fetch schema for selected form
  const fetchSchema = useCallback(async (formOid: string, studyOid_0: string) => {
    setLoadingSchema(true);
    setError(null);
    // Don't clear result/edits here — selectForm may load a saved run
    setResult(null);
    setEdits({});
    setSelectedGroups([]);
    try {
      const data_4 = await apiClient.getFormSchema(formOid, studyOid_0);
      setSchema(data_4);
    } catch (e_8) {
      setError(errorMessage(e_8));
    } finally {
      setLoadingSchema(false);
    }
  }, []);

  // Fetch suite data for a study (runs list for progress bar).
  // L2/L3 persist under the STRIDE study OID — never let that clobber the
  // codebook suite pointer used for L1 Saved Runs / form restore. When the
  // STRIDE suite is fetched in codebook mode, merge Val L2/L3 form_oid runs
  // into suiteRuns so post-save progress refresh still lands.
  const fetchSuite = useCallback(async (studyOid_1: string) => {
    const codebookKey = selectedCodebookFile ? codebookStudyKey(selectedCodebookPath || selectedCodebookFile) : null;
    try {
      const data_5 = await apiClient.getTestCaseSuite(studyOid_1);
      if (codebookKey && studyOid_1 !== codebookKey) {
        const layerRuns = data_5.runs.filter(r => isValLayerFormOid(r.form_oid));
        if (layerRuns.length > 0) {
          setSuiteRuns(prev_0 => mergeSuiteRunsByFormOid(prev_0, layerRuns));
        }
        return data_5;
      }
      setSuite(data_5.suite);
      setSuiteRuns(data_5.runs);
      return data_5;
    } catch {
      if (codebookKey && studyOid_1 !== codebookKey) {
        return {
          suite: null,
          runs: [] as SuiteRun[]
        };
      }
      // Suite doesn't exist yet — that's fine
      setSuite(null);
      setSuiteRuns([]);
      return {
        suite: null,
        runs: [] as SuiteRun[]
      };
    }
  }, [selectedCodebookFile, selectedCodebookPath]);

  // Load a saved L1 run for a specific form_oid (same key as saveRun).
  // Returns true when a run was applied; false when none exists.
  const loadSavedRun = useCallback(async (suiteId: string, formOid_0: string): Promise<boolean> => {
    try {
      const run = await apiClient.getTestCaseRun(suiteId, formOid_0);
      const loaded = run.result_json as TestCaseResult;
      const loadedEdits = run.edits_json ?? {};
      setResult(loaded);
      setEdits(loadedEdits);
      if (run.item_group_oids) setSelectedGroups(run.item_group_oids);
      setSavedL1Fingerprint(fingerprintLayer1(loaded, loadedEdits));
      return true;
    } catch {
      // No saved run — leave empty (caller may already have cleared)
      return false;
    }
  }, []);

  // Fetch codebook suite when the file is selected.
  useEffect(() => {
    if (!selectedCodebookFile) return;
    fetchSuite(codebookStudyKey(selectedCodebookPath || selectedCodebookFile)).catch(() => {});
  }, [selectedCodebookFile, selectedCodebookPath, fetchSuite]);

  // Re-anchor if suite was pointed at another study (historical L2/L3 save bug).
  useEffect(() => {
    if (!selectedCodebookFile || !suite?.study_oid) return;
    const studyKey = codebookStudyKey(selectedCodebookPath || selectedCodebookFile);
    if (suite.study_oid === studyKey) return;
    fetchSuite(studyKey).catch(() => {});
  }, [selectedCodebookFile, selectedCodebookPath, suite?.study_oid, fetchSuite]);

  // Load saved L1 when landing on a form that already has a suite run.
  useEffect(() => {
    if (!restored || !codebookSchema) return;
    if (selectedCodebookForms.length === 0) return;
    if (!selectedCodebookFile) return;
    const studyKey_0 = codebookStudyKey(selectedCodebookPath || selectedCodebookFile);
    // Ignore STRIDE-suite pointers — L1 lives under cb:{folder}.
    if (!suite?.id || suite.study_oid !== studyKey_0) return;
    const formOid_1 = codebookFormOid(selectedCodebookForms);
    let cancelled = false;
    void loadSavedRun(suite.id, formOid_1).then(found => {
      if (cancelled || found) return;
      // Explicit miss on the codebook suite: empty Generate for this form.
      setResult(null);
      setEdits({});
      setSavedL1Fingerprint(null);
    });
    return () => {
      cancelled = true;
    };
  }, [restored, suite?.id, suite?.study_oid, selectedCodebookForms, selectedCodebookFile, selectedCodebookPath, codebookSchema, loadSavedRun]);

  // Save current result + edits to DB
  const saveRun = useCallback(async (): Promise<boolean> => {
    if (!result) return false;
    // Codebook mode: use the codebook's folder path as study, selected forms as form key
    const studyOid_2 = codebookSchema ? codebookStudyKey(selectedCodebookPath || selectedCodebookFile || 'codebook') : selectedStudy;
    const formOid_2 = codebookSchema ? codebookFormOid(selectedCodebookForms) : selectedForm;
    if (!studyOid_2 || !formOid_2) return false;
    setSaving(true);
    try {
      const studyName = codebookSchema ? (selectedCodebookPath || selectedCodebookFile).split('/')[0] || codebookSchema.filename?.replace(/\.[^.]+$/, '') || 'Codebook' : studies.find(s_1 => s_1.OID === selectedStudy)?.StudyName ?? selectedStudy;
      const formName_0 = codebookSchema ? selectedCodebookForms.join(', ') || 'All Forms' : forms.find(f_0 => f_0.form_oid === selectedForm)?.form_name ?? selectedForm;
      await apiClient.saveTestCaseRun({
        study_oid: studyOid_2,
        study_name: studyName,
        form_oid: formOid_2,
        form_name: formName_0,
        result_json: result,
        edits_json: Object.keys(edits).length > 0 ? edits : undefined,
        item_group_oids: selectedGroups.length > 0 ? selectedGroups : undefined,
        codebook_context: codebookSchema ? {
          file_id: selectedCodebookFile,
          source: selectedCodebookSource || codebookSource,
          sheets: codebookSheets,
          sheet: selectedCodebookSheet,
          selected_forms: selectedCodebookForms,
          codebook_type: codebookType,
          codebook_schema: codebookSchema
        } : undefined
      });
      setSavedL1Fingerprint(fingerprintLayer1(result, edits));
      // Refresh suite data so progress bar updates
      await fetchSuite(studyOid_2);
      return true;
    } catch (e_9) {
      setError(`Save failed: ${errorMessage(e_9)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [result, codebookSchema, selectedCodebookFile, selectedCodebookPath, selectedCodebookSource, codebookSource, codebookSheets, selectedCodebookSheet, selectedCodebookForms, codebookType, selectedStudy, selectedForm, edits, selectedGroups, studies, forms, fetchSuite]);

  // Handle study selection
  const selectStudy = useCallback((studyOid_3: string) => {
    setSelectedStudy(studyOid_3);
    if (studyOid_3) {
      fetchForms(studyOid_3);
      fetchSuite(studyOid_3);
    }
  }, [fetchForms, fetchSuite]);

  // Handle form selection
  const selectForm = useCallback((formOid_3: string) => {
    setSelectedForm(formOid_3);
    if (formOid_3 && selectedStudy) {
      fetchSchema(formOid_3, selectedStudy);
      // Try to load saved run
      if (suite?.id) {
        loadSavedRun(suite.id, formOid_3);
      }
    }
  }, [selectedStudy, fetchSchema, suite, loadSavedRun]);

  // Upload codebook
  const uploadCodebook = useCallback(async (file_0: File) => {
    setParsingCodebook(true);
    setError(null);
    try {
      const data_6 = await apiClient.parseCodebook(file_0);
      setCodebookDelta(data_6.changes);
      setCodebookFilename(data_6.filename);
    } catch (e_10) {
      setError(errorMessage(e_10));
    } finally {
      setParsingCodebook(false);
    }
  }, []);

  // Inline edit helpers — must be defined before downloadExcel/generate which reference them
  const getEditedValue = useCallback((categoryName: string, tcId: string, stepIndex: number, field: string, original: string) => {
    const key_0 = `${categoryName}:${tcId}:${stepIndex}:${field}`;
    return edits[key_0] ?? original;
  }, [edits]);
  const setEditedValue = useCallback((categoryName_0: string, tcId_0: string, stepIndex_0: number, field_0: string, value_0: string) => {
    const key_1 = `${categoryName_0}:${tcId_0}:${stepIndex_0}:${field_0}`;
    setEdits(prev_1 => ({
      ...prev_1,
      [key_1]: value_0
    }));
  }, []);

  // Add a new blank test case to a category
  const addTestCase = useCallback((categoryName_1: string) => {
    setResult(prev_2 => {
      if (!prev_2) return prev_2;
      return {
        categories: prev_2.categories.map(cat => {
          if (cat.name !== categoryName_1) return cat;
          // Generate next ID: find highest numeric suffix in this category
          const prefix = categoryName_1 === 'Happy Path' ? 'HP' : categoryName_1 === 'Boundary/Range' ? 'BR' : categoryName_1 === 'Negative' ? 'NEG' : categoryName_1.substring(0, 3).toUpperCase();
          const existingNums = cat.test_cases.map(tc => {
            const m = tc.id.match(/-(\d+)$/);
            return m ? parseInt(m[1], 10) : 0;
          });
          const nextNum = Math.max(0, ...existingNums) + 1;
          const newId = `${prefix}-${String(nextNum).padStart(3, '0')}`;
          return {
            ...cat,
            test_cases: [...cat.test_cases, {
              id: newId,
              title: 'New test case',
              steps: [{
                step: 1,
                action: '',
                field: '',
                input: '',
                expected: ''
              }]
            }]
          };
        })
      };
    });
  }, []);

  // Delete test cases by ID from a category
  const deleteTestCases = useCallback((categoryName_2: string, tcIds: string[]) => {
    const idSet = new Set(tcIds);
    setResult(prev_3 => {
      if (!prev_3) return prev_3;
      return {
        categories: prev_3.categories.map(cat_0 => {
          if (cat_0.name !== categoryName_2) return cat_0;
          return {
            ...cat_0,
            test_cases: cat_0.test_cases.filter(tc_0 => !idSet.has(tc_0.id))
          };
        })
      };
    });
    // Also clean up any edits for deleted test cases
    setEdits(prev_4 => {
      const next_0 = {
        ...prev_4
      };
      for (const key_2 of Object.keys(next_0)) {
        for (const id of tcIds) {
          if (key_2.startsWith(`${categoryName_2}:${id}:`)) {
            delete next_0[key_2];
          }
        }
      }
      return next_0;
    });
  }, []);

  // Add a step to an existing test case
  const addStepToTestCase = useCallback((categoryName_3: string, tcId_1: string) => {
    setResult(prev_5 => {
      if (!prev_5) return prev_5;
      return {
        categories: prev_5.categories.map(cat_1 => {
          if (cat_1.name !== categoryName_3) return cat_1;
          return {
            ...cat_1,
            test_cases: cat_1.test_cases.map(tc_1 => {
              if (tc_1.id !== tcId_1) return tc_1;
              const nextStep = (tc_1.steps?.length ?? 0) + 1;
              return {
                ...tc_1,
                steps: [...(tc_1.steps ?? []), {
                  step: nextStep,
                  action: '',
                  field: '',
                  input: '',
                  expected: ''
                }]
              };
            })
          };
        })
      };
    });
  }, []);

  // Delete a step from a test case (won't delete the last remaining step)
  const deleteStepFromTestCase = useCallback((categoryName_4: string, tcId_2: string, stepIndex_1: number) => {
    setResult(prev_6 => {
      if (!prev_6) return prev_6;
      return {
        categories: prev_6.categories.map(cat_2 => {
          if (cat_2.name !== categoryName_4) return cat_2;
          return {
            ...cat_2,
            test_cases: cat_2.test_cases.map(tc_2 => {
              if (tc_2.id !== tcId_2) return tc_2;
              if ((tc_2.steps?.length ?? 0) <= 1) return tc_2; // keep at least one step
              const newSteps = tc_2.steps.filter((_, i) => i !== stepIndex_1).map((s_2, i_0) => ({
                ...s_2,
                step: i_0 + 1
              })); // renumber
              return {
                ...tc_2,
                steps: newSteps
              };
            })
          };
        })
      };
    });
    // Clean up edits for removed step and shift subsequent step edits
    setEdits(prev_7 => {
      const next_1 = {
        ...prev_7
      };
      const prefix_0 = `${categoryName_4}:${tcId_2}:`;
      for (const key_3 of Object.keys(next_1)) {
        if (!key_3.startsWith(prefix_0)) continue;
        const rest = key_3.slice(prefix_0.length);
        const colonIdx = rest.indexOf(':');
        const si = parseInt(rest.slice(0, colonIdx), 10);
        const field_1 = rest.slice(colonIdx + 1);
        if (si === stepIndex_1) {
          delete next_1[key_3];
        } else if (si > stepIndex_1) {
          // Shift down
          delete next_1[key_3];
          next_1[`${prefix_0}${si - 1}:${field_1}`] = prev_7[key_3];
        }
      }
      return next_1;
    });
  }, []);

  // Generate test cases
  const generate = useCallback(async () => {
    if (!selectedStudy || !selectedForm) return;
    setGenerating(true);
    setError(null);
    setEdits({});
    setResult(null);
    setGenerationPhase('schema');
    await delay(1500);
    setGenerationPhase('prompt');
    await delay(2000);
    setGenerationPhase('generating');
    try {
      const data_7 = await apiClient.generateTestCases({
        study_oid: selectedStudy,
        form_oid: selectedForm,
        item_group_oids: selectedGroups.length > 0 ? selectedGroups : undefined,
        codebook_delta: codebookDelta || undefined,
        custom_instructions: customInstructions || undefined
      });
      setGenerationPhase('parsing');
      await delay(1000);
      setGenerationPhase('formatting');
      await delay(500);
      const tcResult = data_7 as TestCaseResult;
      setResult(tcResult);

      // Auto-save to DB after successful generation
      const studyName_0 = studies.find(s_3 => s_3.OID === selectedStudy)?.StudyName ?? selectedStudy;
      const formName_1 = forms.find(f_1 => f_1.form_oid === selectedForm)?.form_name ?? selectedForm;
      apiClient.saveTestCaseRun({
        study_oid: selectedStudy,
        study_name: studyName_0,
        form_oid: selectedForm,
        form_name: formName_1,
        result_json: tcResult,
        item_group_oids: selectedGroups.length > 0 ? selectedGroups : undefined
      }).then(() => fetchSuite(selectedStudy)).catch(() => {});
    } catch (e_11) {
      setError(errorMessage(e_11));
    } finally {
      setGenerating(false);
      setGenerationPhase('idle');
    }
  }, [selectedStudy, selectedForm, selectedGroups, codebookDelta, customInstructions, studies, forms, fetchSuite]);

  // Download Excel — sends current result with edits applied to backend for openpyxl formatting
  const downloadExcel = useCallback(async () => {
    if (!result) return;
    if (!selectedForm && !codebookSchema) return;
    try {
      // Build result with edits baked in
      const editedResult: TestCaseResult = {
        categories: result.categories.map(cat_3 => ({
          ...cat_3,
          test_cases: cat_3.test_cases.map(tc_3 => ({
            ...tc_3,
            steps: tc_3.steps?.map((step, si_0) => ({
              step: step.step,
              action: getEditedValue(cat_3.name, tc_3.id, si_0, 'action', step.action),
              field: getEditedValue(cat_3.name, tc_3.id, si_0, 'field', step.field),
              input: getEditedValue(cat_3.name, tc_3.id, si_0, 'input', step.input),
              expected: getEditedValue(cat_3.name, tc_3.id, si_0, 'expected', step.expected)
            })) ?? []
          }))
        }))
      };
      const studyName_1 = codebookSchema ? codebookSchema.filename?.replace(/\.[^.]+$/, '') || 'Codebook' : studies.find(s_4 => s_4.OID === selectedStudy)?.StudyName;
      const formName_2 = codebookSchema ? selectedCodebookForms.join(', ') || 'All Forms' : forms.find(f_2 => f_2.form_oid === selectedForm)?.form_name;
      const formOid_4 = selectedForm || 'codebook';
      const blob_0 = await apiClient.exportTestCasesExcel(editedResult, formOid_4, studyName_1, formName_2);
      const url_0 = URL.createObjectURL(blob_0);
      const a_0 = document.createElement('a');
      a_0.href = url_0;
      const downloadName = codebookSchema ? `${(studyName_1 || 'Codebook').replace(/[^a-zA-Z0-9_-]/g, '_')}_Test_Cases.xlsx` : `${selectedForm.replace('.', '_')}_Test_Cases.xlsx`;
      a_0.download = downloadName;
      document.body.appendChild(a_0);
      a_0.click();
      document.body.removeChild(a_0);
      URL.revokeObjectURL(url_0);
    } catch (e_12) {
      setError(errorMessage(e_12));
    }
  }, [result, selectedForm, selectedStudy, studies, forms, getEditedValue, codebookSchema, selectedCodebookForms]);

  // Toggle item group
  const toggleGroup = useCallback((oid: string) => {
    setSelectedGroups(prev_8 => prev_8.includes(oid) ? prev_8.filter(g => g !== oid) : [...prev_8, oid]);
  }, []);

  // Fetch studies on provider mount (once)
  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  // ── Session restore on mount ────────────────────────────────────────
  // Restores codebook-mode or STRIDE-mode session from localStorage + saved runs.
  useEffect(() => {
    if (restored) return;
    if (typeof window === 'undefined') return;
    const params_0 = new URLSearchParams(window.location.search);
    if (params_0.get('mock') === 'true') return; // mock mode handles its own state

    const savedCbFile = localStorage.getItem(LS_CB_FILE);
    const savedCbPath = localStorage.getItem(LS_CB_PATH) || savedCbFile || '';
    const savedCbSource = localStorage.getItem(LS_CB_SOURCE) || '';
    const savedCbSheet = localStorage.getItem(LS_CB_SHEET);
    const savedCbForms = localStorage.getItem(LS_CB_FORMS);

    // ── Codebook-mode restore ──
    if (savedCbFile) {
      const savedLabel = savedCbFile.split('/').pop() ?? savedCbFile;
      const looksLikeGraphId = !savedCbFile.includes('/') && !savedCbFile.includes('.') && savedCbFile.length >= 20;
      if (!looksLikeGraphId && !isCodebookFileName(savedLabel)) {
        localStorage.removeItem(LS_CB_FILE);
        localStorage.removeItem(LS_CB_SOURCE);
        localStorage.removeItem(LS_CB_SHEET);
        localStorage.removeItem(LS_CB_FORMS);
        setRestored(true);
        return;
      }
      let cancelled_0 = false;
      // Own an epoch so a superseded hydrate can't leave Loading… stuck, and so
      // a later selectCodebookFile bump correctly invalidates this in-flight work.
      // Inline the bump (don't call bumpCodebookEpoch) so this effect's deps stay
      // `[restored]` — changing dep array size mid-HMR throws in React. Still
      // clear spinners here for the same deadlock reason as bumpCodebookEpoch.
      codebookEpochRef.current += 1;
      setLoadingCodebookSheets(false);
      setLoadingCodebookSchema(false);
      const epochAtStart = codebookEpochRef.current;
      const parsedSavedForms = (() => {
        if (!savedCbForms) return [] as string[];
        try {
          return JSON.parse(savedCbForms) as string[];
        } catch {
          return [];
        }
      })();
      const hydrateCodebookSheets = async () => {
        const applySchema = (schema_3: CodebookSchema) => {
          // Ignore `cancelled` — React Strict Mode aborts the first mount while
          // its SharePoint parse is often the one that returns 200; the remount
          // twin may 502. Keep the good result if this file/sheet is still selected.
          if (selectedCodebookFileRef.current !== savedCbFile) return;
          const epochOk_0 = codebookEpochRef.current === epochAtStart;
          const sheetOk_0 = !schema_3.sheet || !selectedCodebookSheetRef.current || schema_3.sheet === selectedCodebookSheetRef.current;
          if (!epochOk_0 && !sheetOk_0) return;
          setCodebookSchema(schema_3);
          if (schema_3.sheets_available?.length) setCodebookSheets(schema_3.sheets_available);
          if (schema_3.sheet) setSelectedCodebookSheet(schema_3.sheet);
          setLoadingCodebookSchema(false);
          setError(null);
          // Re-apply saved form only after a version/schema exists.
          if (parsedSavedForms.length && schema_3.forms?.length) {
            const names = new Set(schema_3.forms.map(f_3 => f_3.form_name));
            const valid = parsedSavedForms.filter(n => names.has(n));
            if (valid.length) setSelectedCodebookForms(valid);
          }
        };
        const spToken_4 = await getSharePointTokenRef.current();
        const parseOnce = async (sheet_2: string | undefined) => {
          const flightKey_0 = `${savedCbFile}|${sheet_2 || 'csv'}|${codebookTypeRef.current}`;
          if (inFlightParseKeyRef.current === flightKey_0) return;
          inFlightParseKeyRef.current = flightKey_0;
          setLoadingCodebookSchema(true);
          try {
            const schema_4 = await apiClient.parseCodebookFromBlob(savedCbFile, sheet_2 || undefined, savedCbSource || undefined, spToken_4, codebookTypeRef.current);
            applySchema(schema_4);
          } catch (e_13) {
            const alreadyHaveForms_0 = (codebookSchemaRef.current?.forms?.length ?? 0) > 0;
            if (!alreadyHaveForms_0 && selectedCodebookFileRef.current === savedCbFile) {
              setError(formatCodebookError(errorMessage(e_13)));
            }
          } finally {
            if (inFlightParseKeyRef.current === flightKey_0) inFlightParseKeyRef.current = null;
            setLoadingCodebookSchema(false);
          }
        };
        if (savedCbFile.toLowerCase().endsWith('.csv')) {
          await parseOnce(undefined);
          return;
        }
        setLoadingCodebookSheets(true);
        try {
          const data_8 = await apiClient.getCodebookSheets(savedCbFile, savedCbSource || undefined, spToken_4);
          if (selectedCodebookFileRef.current !== savedCbFile) return;
          if (data_8.sheets.length === 0) {
            await parseOnce(undefined);
            return;
          }

          // Sheets list is cheap and stable — apply even if a later bump raced us,
          // as long as this is still the restored file.
          setCodebookSheets(data_8.sheets);
          const sheet_3 = savedCbSheet && data_8.sheets.includes(savedCbSheet) ? savedCbSheet : data_8.sheets.length === 1 ? data_8.sheets[0] : '';
          if (!sheet_3) {
            // Multi-sheet workbook and no usable saved version — don't leave a
            // stale form selection painting step 3 done while version is empty.
            setSelectedCodebookSheet('');
            setSelectedCodebookForms([]);
            return;
          }
          setSelectedCodebookSheet(sheet_3);
          await parseOnce(sheet_3);
        } catch (e_14) {
          if (selectedCodebookFileRef.current !== savedCbFile) return;
          try {
            await parseOnce(undefined);
          } catch (parseErr) {
            const alreadyHaveForms_1 = (codebookSchemaRef.current?.forms?.length ?? 0) > 0;
            if (!alreadyHaveForms_1) {
              setError(formatCodebookError(errorMessage(parseErr, errorMessage(e_14))));
            }
          }
        } finally {
          // Always release sheets spinner — epoch-gated clear left VERSION stuck
          // on Loading… after a restore race.
          setLoadingCodebookSheets(false);
        }
      };

      // 1. Immediately restore file (+ sheet when we have it). Defer form
      //    selection until a version/schema exists — otherwise the stepper marks
      //    "Pick a Form" done while VERSION still says Loading…
      setSelectedCodebookFile(savedCbFile);
      setSelectedCodebookPath(savedCbPath);
      setSelectedCodebookSource(savedCbSource);
      if (savedCbSheet) setSelectedCodebookSheet(savedCbSheet);
      // CSV has no sheet step — restore forms immediately.
      if (savedCbFile.toLowerCase().endsWith('.csv') && parsedSavedForms.length) {
        setSelectedCodebookForms(parsedSavedForms);
      }

      // 2. Hydrate sheets/schema NOW so the form picker appears even while the
      //    suite lookup is slow or 404s. Waiting on getTestCaseSuite first left
      //    step 3 lit with VERSION/FORMS hidden in the sidebar.
      void hydrateCodebookSheets();

      // 3. Load saved run in parallel (results / context may enrich hydrate).
      const studyKey_1 = codebookStudyKey(savedCbPath);
      const formOid_5 = parsedSavedForms.length ? parsedSavedForms.slice().sort().join('+') || 'all-forms' : 'all-forms';
      apiClient.getTestCaseSuite(studyKey_1).then(suiteData => {
        if (cancelled_0 || codebookEpochRef.current !== epochAtStart) return;
        setSuite(suiteData.suite);
        setSuiteRuns(suiteData.runs as SuiteRun[]);
        if (suiteData.suite?.id) {
          return apiClient.getTestCaseRun(suiteData.suite.id, formOid_5);
        }
      }).then(run_0 => {
        if (cancelled_0 || codebookEpochRef.current !== epochAtStart) return;
        if (!run_0) return;
        const loaded_0 = run_0.result_json as TestCaseResult;
        const loadedEdits_0 = run_0.edits_json ?? {};
        setResult(loaded_0);
        setEdits(loadedEdits_0);
        setSavedL1Fingerprint(fingerprintLayer1(loaded_0, loadedEdits_0));
        const ctx = run_0.codebook_context;
        if (ctx) {
          if (ctx.sheets) setCodebookSheets(ctx.sheets);
          if (ctx.sheet) setSelectedCodebookSheet(ctx.sheet);
          if (ctx.selected_forms?.length) setSelectedCodebookForms(ctx.selected_forms);
          const savedType = ctx.codebook_type;
          if (savedType === 'anvil' || savedType === 'l3phc') {
            codebookTypeRef.current = savedType;
            setCodebookTypeState(savedType);
          }
          if (ctx.codebook_schema) setCodebookSchema(ctx.codebook_schema);
        }
      }).catch((err: unknown) => {
        // No saved suite/run — that's fine for the UX. But on 404 from
        // getTestCaseRun, the localStorage form-selection has gone stale
        // (e.g. multi-form composite that was never saved as a run, or a
        // run that was deleted). Clear LS_CB_FORMS so the next page-load
        // doesn't re-poke the dead pointer and emit another 404. Without
        // this clear, every page-load by the same user produces another
        // 404 in App Insights for the lifetime of the localStorage.
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('404')) {
          try {
            localStorage.removeItem(LS_CB_FORMS);
          } catch {/* SSR / disabled storage */}
        }
      }).finally(() => {
        if (!cancelled_0 && codebookEpochRef.current === epochAtStart) setRestored(true);
      });
      return () => {
        cancelled_0 = true;
      };
    }

    // ── STRIDE DB mode restore ──
    const savedStudy = localStorage.getItem(LS_STUDY);
    const savedForm = localStorage.getItem(LS_FORM);
    if (!savedStudy) {
      setRestored(true);
      return;
    }
    let cancelled_1 = false;
    async function restore() {
      try {
        setSelectedStudy(savedStudy!);
        setLoadingForms(true);
        const [formsData, suiteData_0] = await Promise.all([apiClient.getTestCaseForms(savedStudy!), apiClient.getTestCaseSuite(savedStudy!).catch(() => ({
          suite: null,
          runs: []
        }))]);
        if (cancelled_1) return;
        const seenR = new Set<string>();
        const uniqueR = formsData.forms.filter((f_4: Form) => {
          if (seenR.has(f_4.form_oid)) return false;
          seenR.add(f_4.form_oid);
          return true;
        });
        setForms(uniqueR);
        setLoadingForms(false);
        setSuite(suiteData_0.suite);
        setSuiteRuns(suiteData_0.runs as SuiteRun[]);
        if (savedForm && formsData.forms.some((f_5: Form) => f_5.form_oid === savedForm)) {
          setSelectedForm(savedForm);
          setLoadingSchema(true);
          try {
            const schemaData = await apiClient.getFormSchema(savedForm, savedStudy!);
            if (!cancelled_1) setSchema(schemaData);
          } catch {} finally {
            if (!cancelled_1) setLoadingSchema(false);
          }
          if (suiteData_0.suite?.id) {
            try {
              const run_1 = await apiClient.getTestCaseRun(suiteData_0.suite.id, savedForm);
              if (!cancelled_1) {
                setResult(run_1.result_json as TestCaseResult);
                setEdits(run_1.edits_json ?? {});
                if (run_1.item_group_oids) setSelectedGroups(run_1.item_group_oids);
              }
            } catch {}
          }
        }
      } catch (e_15) {
        if (!cancelled_1) console.warn('[TC Restore] Session restore failed:', errorMessage(e_15));
      } finally {
        if (!cancelled_1) setRestored(true);
      }
    }
    restore();
    return () => {
      cancelled_1 = true;
    };
  }, [restored]);

  // Load STRIDE forms for the matched study without touching codebook/result state
  const fetchStrideFormsOnly = useCallback(async (studyOid_4: string) => {
    if (!studyOid_4) return;
    try {
      const data_9 = await apiClient.getTestCaseForms(studyOid_4);
      const seen_1 = new Set<string>();
      const unique_0 = data_9.forms.filter((f_6: {
        form_oid: string;
      }) => {
        if (seen_1.has(f_6.form_oid)) return false;
        seen_1.add(f_6.form_oid);
        return true;
      });
      setForms(unique_0);
    } catch {
      // Non-fatal — form matching will fall back to name-only
    }
  }, []);

  // Normalize a string for fuzzy form name matching
  const _normForm = (s_5: string) => s_5.toLowerCase().replace(/[^a-z0-9]/g, '');
  const generateScenarios = useCallback(async () => {
    if (!result) {
      setError('Generate a Layer 1 test plan first.');
      return;
    }
    if (!strideMatchedStudy) {
      setError('No STRIDE study matched — select one in the sidebar.');
      return;
    }
    if (!selectedCodebookForms[0]) {
      setError('Select a form in the sidebar first.');
      return;
    }
    setGeneratingScenarios(true);
    setScenarioComplianceGaps([]);
    setScenarioProgress(null);
    setScenarioStats(null);
    setScenarioTokenCount(0);
    scenarioTokenRef.current = 0;
    setScenarioStreamText('');
    scenarioStreamRef.current = '';
    setSavedL2Fingerprint(null);
    setActiveLayer('layer2');
    setError(null);
    try {
      // Fuzzy-match selected codebook form name → STRIDE form OID
      const normSelected = _normForm(selectedCodebookForms[0]);
      const matchedForm = forms.find(f_7 => {
        const n_0 = _normForm(f_7.form_name);
        return n_0 === normSelected || n_0.includes(normSelected) || normSelected.includes(n_0);
      });
      const response = await apiClient.generateQAScenarios({
        study_oid: strideMatchedStudy.oid,
        form_oid: matchedForm?.form_oid ?? null,
        codebook_schema: codebookSchema ?? undefined,
        layer1_results: result,
        selected_form_name: selectedCodebookForms[0]
      });

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (reader) {
        const {
          done,
          value: value_1
        } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value_1, {
          stream: true
        });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data_10 = JSON.parse(line.slice(6));
            if (data_10.type === 'progress') {
              setScenarioProgress({
                message: data_10.message,
                step: data_10.step,
                total: data_10.total_steps,
                n_mappings: data_10.n_mappings,
                n_ncdr: data_10.n_ncdr,
                max_tokens: data_10.max_tokens,
                item_count: data_10.item_count,
                prompt_chars: data_10.prompt_chars
              });
            } else if (data_10.type === 'text') {
              scenarioTokenRef.current += 1;
              scenarioStreamRef.current += data_10.content;
              if (scenarioTokenRef.current % 20 === 0) {
                setScenarioTokenCount(scenarioTokenRef.current);
                setScenarioStreamText(scenarioStreamRef.current);
              }
            } else if (data_10.type === 'stats') {
              setScenarioTokenCount(scenarioTokenRef.current);
              setScenarioStats({
                input_tokens: data_10.input_tokens,
                output_tokens: data_10.output_tokens
              });
            } else if (data_10.type === 'result') {
              const content = data_10.content as {
                scenarios?: ScenarioResult[];
                compliance_gaps?: ComplianceGap[];
              } | undefined;
              if (content?.scenarios) setScenarioResults(content.scenarios);
              setScenarioComplianceGaps(content?.compliance_gaps ?? []);
            }
          } catch {/* partial chunk */}
        }
      }
    } catch (e_16) {
      setError(errorMessage(e_16));
    } finally {
      setGeneratingScenarios(false);
      setScenarioProgress(null);
    }
  }, [strideMatchedStudy, selectedCodebookForms, result, forms, codebookSchema]);
  const setScenarioReview = useCallback((id_0: string, review: ScenarioReview) => {
    setScenarioReviews(prev_9 => ({
      ...prev_9,
      [id_0]: review
    }));
  }, []);
  const saveScenarioSuite = useCallback(async (): Promise<boolean> => {
    if (!scenarioResults || !strideMatchedStudy) return false;
    setSavingScenarios(true);
    try {
      const formName_3 = selectedCodebookForms[0] ?? 'All Forms';
      // Store full scenario set + review states — approved/rejected both preserved for audit
      await apiClient.saveTestCaseRun({
        study_oid: strideMatchedStudy.oid,
        study_name: strideMatchedStudy.name,
        form_oid: `val:scenarios:${formName_3}`,
        form_name: `Val Scenarios — ${formName_3}`,
        result_json: {
          scenarios: scenarioResults,
          reviews: scenarioReviews,
          // Layer 3 needs these; without them a reload silently drops the
          // compliance gaps Layer 2 found.
          compliance_gaps: scenarioComplianceGaps,
          approved_count: scenarioResults.filter(s_6 => scenarioReviews[s_6.id]?.status === 'approved').length,
          saved_at: new Date().toISOString()
        } satisfies SavedScenarioSuite,
        edits_json: Object.fromEntries(Object.entries(scenarioReviews).map(([id_1, r_0]) => [id_1, JSON.stringify(r_0)]))
      });
      setSavedL2Fingerprint(fingerprintLayer2(scenarioResults, scenarioReviews, scenarioComplianceGaps));
      // Refresh suite so layer bar reflects the saved run
      await fetchSuite(strideMatchedStudy.oid);
      return true;
    } catch (e_17) {
      setError(errorMessage(e_17));
      return false;
    } finally {
      setSavingScenarios(false);
    }
  }, [scenarioResults, scenarioReviews, scenarioComplianceGaps, strideMatchedStudy, selectedCodebookForms, fetchSuite]);

  // Restore scenario suite when study + form are both known
  useEffect(() => {
    if (!strideOid || !firstSelectedForm) return;
    const formOid_6 = `val:scenarios:${firstSelectedForm}`;
    let cancelled_2 = false;
    apiClient.getTestCaseSuite(strideOid).then(suiteData_1 => {
      if (cancelled_2 || !suiteData_1.suite?.id) return;
      return apiClient.getTestCaseRun(suiteData_1.suite.id, formOid_6);
    }).then(run_2 => {
      if (cancelled_2 || !run_2) return;
      const payload = run_2.result_json as SavedScenarioSuite | null;
      if (payload?.scenarios) setScenarioResults(payload.scenarios);
      if (payload?.reviews) setScenarioReviews(payload.reviews);
      if (payload?.compliance_gaps) setScenarioComplianceGaps(payload.compliance_gaps);
      if (payload?.scenarios) {
        setSavedL2Fingerprint(fingerprintLayer2(payload.scenarios, payload.reviews ?? {}, payload.compliance_gaps ?? []));
      }
    }).catch(() => {/* no saved run — fresh state is fine */});
    return () => {
      cancelled_2 = true;
    };
  }, [strideOid, firstSelectedForm]);
  const generateGaps = useCallback(async () => {
    if (!result) {
      setError('Generate a Layer 1 test plan first.');
      return;
    }
    if (!scenarioResults) {
      setError('Generate Layer 2 scenarios first.');
      return;
    }
    setGapDispositions({});
    setGeneratingGaps(true);
    setGapProgress(null);
    setGapStats(null);
    setGapTokenCount(0);
    gapTokenRef.current = 0;
    setGapStreamText('');
    gapStreamRef.current = '';
    setGapResults(null);
    setSavedL3Fingerprint(null);
    setActiveLayer('layer3');
    setError(null);
    try {
      // Send lean summaries — full step-by-step data bloats the prompt unnecessarily
      const layer1Summary = {
        categories: result.categories.map(cat_4 => ({
          name: cat_4.name,
          test_case_count: cat_4.test_cases.length,
          // Include titles only so LLM knows what was covered, not every step
          test_cases: cat_4.test_cases.map(tc_4 => ({
            id: tc_4.id,
            title: tc_4.title
          }))
        }))
      };
      const approvedScenarios = scenarioResults.filter(s_7 => scenarioReviews[s_7.id]?.status === 'approved');
      // The generator returns compliance gaps once, next to `scenarios` — not
      // per scenario — and `_build_gaps_prompt` reads them off the top level.
      // Harvesting them from each scenario (as this used to) always produced an
      // empty list, so Layer 3 ran with "None reported by Layer 2" every time.
      const layer2Payload = {
        scenarios: approvedScenarios.map(s_8 => ({
          id: s_8.id,
          title: s_8.title,
          ncdr_elements: s_8.ncdr_elements ?? []
        })),
        compliance_gaps: scenarioComplianceGaps
      };
      const response_0 = await apiClient.generateQAGaps({
        study_oid: strideMatchedStudy?.oid,
        layer1_results: layer1Summary,
        layer2_results: layer2Payload
      });
      const reader_0 = response_0.body?.getReader();
      const decoder_0 = new TextDecoder();
      let buffer_0 = '';
      while (reader_0) {
        const {
          done: done_0,
          value: value_2
        } = await reader_0.read();
        if (done_0) break;
        buffer_0 += decoder_0.decode(value_2, {
          stream: true
        });
        const lines_0 = buffer_0.split('\n');
        buffer_0 = lines_0.pop() ?? '';
        for (const line_0 of lines_0) {
          if (!line_0.startsWith('data: ')) continue;
          try {
            const data_11 = JSON.parse(line_0.slice(6));
            if (data_11.type === 'progress') {
              setGapProgress({
                message: data_11.message,
                step: data_11.step,
                total: data_11.total_steps,
                n_mappings: data_11.n_mappings,
                n_ncdr: data_11.n_ncdr,
                max_tokens: data_11.max_tokens,
                item_count: data_11.item_count,
                prompt_chars: data_11.prompt_chars
              });
            } else if (data_11.type === 'text') {
              gapTokenRef.current += 1;
              gapStreamRef.current += data_11.content;
              if (gapTokenRef.current % 20 === 0) {
                setGapTokenCount(gapTokenRef.current);
                setGapStreamText(gapStreamRef.current);
              }
            } else if (data_11.type === 'stats') {
              setGapTokenCount(gapTokenRef.current);
              setGapStats({
                input_tokens: data_11.input_tokens,
                output_tokens: data_11.output_tokens
              });
            } else if (data_11.type === 'result') {
              const content_0 = data_11.content as {
                gaps?: GapResult[];
                unmapped_ncdr_elements?: UnmappedNcdrElement[];
              } | undefined;
              const gaps: GapResult[] = [...(content_0?.gaps ?? []), ...(content_0?.unmapped_ncdr_elements ?? []).map(e_19 => ({
                gap_id: `UNMAP-${e_19.element_number}`,
                severity: e_19.severity ?? 'HIGH',
                title: `Unmapped: ${e_19.element_name}`,
                description: e_19.coding_instruction ?? '',
                gap_type: 'MISSING_FIELD',
                remediation: e_19.remediation,
                ncdr_element_number: e_19.element_number,
                ncdr_element_name: e_19.element_name,
                coding_instruction: e_19.coding_instruction,
                evidence: `No ItemDef mapped to NCDR element ${e_19.element_number} (${e_19.element_name})`
              }))];
              setGapResults(gaps);
            }
          } catch {/* partial chunk */}
        }
      }
    } catch (e_18) {
      setError(errorMessage(e_18));
    } finally {
      setGeneratingGaps(false);
      setGapProgress(null);
    }
  }, [result, scenarioResults, scenarioReviews, scenarioComplianceGaps, strideMatchedStudy]);
  const setGapDisposition = useCallback((id_2: string, d: GapDisposition) => {
    setGapDispositions(prev_10 => ({
      ...prev_10,
      [id_2]: d
    }));
  }, []);
  const saveGapSuite = useCallback(async (): Promise<boolean> => {
    if (!gapResults || !strideMatchedStudy) return false;
    setSavingGaps(true);
    try {
      const formName_4 = selectedCodebookForms[0] ?? 'All Forms';
      await apiClient.saveTestCaseRun({
        study_oid: strideMatchedStudy.oid,
        study_name: strideMatchedStudy.name,
        form_oid: `val:gaps:${formName_4}`,
        form_name: `Val Gap Analysis — ${formName_4}`,
        result_json: {
          gaps: gapResults,
          dispositions: gapDispositions,
          summary: {
            total: gapResults.length,
            applied: Object.values(gapDispositions).filter(d_0 => d_0 === 'applied').length,
            confirmed: Object.values(gapDispositions).filter(d_1 => d_1 === 'confirmed').length,
            dismissed: Object.values(gapDispositions).filter(d_2 => d_2 === 'dismissed').length
          },
          saved_at: new Date().toISOString()
        } satisfies SavedGapSuite,
        edits_json: Object.fromEntries(Object.entries(gapDispositions).map(([id_3, d_3]) => [id_3, d_3]))
      });
      setSavedL3Fingerprint(fingerprintLayer3(gapResults, gapDispositions));
      await fetchSuite(strideMatchedStudy.oid);
      return true;
    } catch (e_20) {
      setError(errorMessage(e_20));
      return false;
    } finally {
      setSavingGaps(false);
    }
  }, [gapResults, gapDispositions, strideMatchedStudy, selectedCodebookForms, fetchSuite]);

  // Restore gap suite when study + form are known
  useEffect(() => {
    if (!strideOid || !firstSelectedForm) return;
    const formOid_7 = `val:gaps:${firstSelectedForm}`;
    let cancelled_3 = false;
    apiClient.getTestCaseSuite(strideOid).then(suiteData_2 => {
      if (cancelled_3 || !suiteData_2.suite?.id) return;
      return apiClient.getTestCaseRun(suiteData_2.suite.id, formOid_7);
    }).then(run_3 => {
      if (cancelled_3 || !run_3) return;
      const payload_0 = run_3.result_json as SavedGapSuite | null;
      if (payload_0?.gaps) setGapResults(payload_0.gaps);
      if (payload_0?.dispositions) setGapDispositions(payload_0.dispositions);
      if (payload_0?.gaps) {
        setSavedL3Fingerprint(fingerprintLayer3(payload_0.gaps, payload_0.dispositions ?? {}));
      }
    }).catch(() => {});
    return () => {
      cancelled_3 = true;
    };
  }, [strideOid, firstSelectedForm]);
  const applyGapFix = useCallback(async (gapId: string) => {
    if (!gapResults || !result) return;
    const gap = gapResults.find(g_0 => g_0.gap_id === gapId);
    if (!gap) return;
    const {
      generateApplyFix,
      insertL1Fix
    } = await import('@/components/test-cases/_stride/gap-fix-generator');
    const fix = generateApplyFix(gap, result, scenarioResults ?? []);
    if (!fix) {
      setAppliedGaps(prev_11 => new Set([...prev_11, gapId]));
      return;
    }
    if (fix.layer === 1) {
      setResult(prev_12 => prev_12 ? insertL1Fix(prev_12, fix.category, fix.testCase) : prev_12);
    } else {
      setScenarioResults(prev_13 => prev_13 ? [...prev_13, fix.scenario] : [fix.scenario]);
    }
    setAppliedGaps(prev_14 => new Set([...prev_14, gapId]));
    setGapDisposition(gapId, 'applied');
  }, [gapResults, result, scenarioResults, setGapDisposition]);
  const hasPlanWork = !!(result || scenarioResults || gapResults);
  const isPlanComplete = !!gapResults && !generatingGaps;
  const currentL1Fp = useMemo(() => result ? fingerprintLayer1(result, edits) : null, [result, edits]);
  const currentL2Fp = useMemo(() => scenarioResults ? fingerprintLayer2(scenarioResults, scenarioReviews, scenarioComplianceGaps) : null, [scenarioResults, scenarioReviews, scenarioComplianceGaps]);
  const currentL3Fp = useMemo(() => gapResults ? fingerprintLayer3(gapResults, gapDispositions) : null, [gapResults, gapDispositions]);
  const isDirty = useMemo(() => isPlanDirty({
    l1: isLayerDirty(!!result, currentL1Fp, savedL1Fingerprint),
    l2: isLayerDirty(!!scenarioResults, currentL2Fp, savedL2Fingerprint),
    l3: isLayerDirty(!!gapResults, currentL3Fp, savedL3Fingerprint)
  }), [result, scenarioResults, gapResults, currentL1Fp, currentL2Fp, currentL3Fp, savedL1Fingerprint, savedL2Fingerprint, savedL3Fingerprint]);
  const requestNewTestPlan = useCallback(() => {
    if (isDirty) {
      setNewPlanGuardOpen(true);
      return;
    }
    clearWork();
  }, [isDirty, clearWork]);
  const savePlanForLeave = useCallback(async () => {
    setSavingPlanForLeave(true);
    setError(null);
    try {
      const l1Dirty = isLayerDirty(!!result, currentL1Fp, savedL1Fingerprint);
      const l2Dirty = isLayerDirty(!!scenarioResults, currentL2Fp, savedL2Fingerprint);
      const l3Dirty = isLayerDirty(!!gapResults, currentL3Fp, savedL3Fingerprint);
      // Leave path: persist whatever is dirty (no review-complete gates).
      if (l1Dirty && result) {
        if (!(await saveRun())) return;
      }
      if (l2Dirty && scenarioResults) {
        if (!(await saveScenarioSuite())) return;
      }
      if (l3Dirty && gapResults) {
        if (!(await saveGapSuite())) return;
      }
      clearWork();
    } finally {
      setSavingPlanForLeave(false);
    }
  }, [result, scenarioResults, gapResults, currentL1Fp, currentL2Fp, currentL3Fp, savedL1Fingerprint, savedL2Fingerprint, savedL3Fingerprint, saveRun, saveScenarioSuite, saveGapSuite, clearWork]);
  const value_3 = useMemo<TestCaseContextValue>(() => ({
    mode: codebookSchema ? 'codebook' as const : 'stride' as const,
    codebookTree,
    codebookSource,
    codebookFiles,
    selectedCodebookFile,
    selectedCodebookPath,
    codebookSheets,
    selectedCodebookSheet,
    codebookType,
    setCodebookType,
    codebookSchema,
    codebookForms,
    selectedCodebookForms,
    loadingCodebookTree,
    loadingCodebookSheets,
    loadingCodebookSchema,
    selectCodebookFile,
    refreshCodebookTree,
    selectCodebookSheet,
    toggleCodebookForm,
    generateManual,
    downloadADO,
    importXlsx,
    clearSession,
    clearWork,
    hasPlanWork,
    isPlanComplete,
    isDirty,
    requestNewTestPlan,
    newPlanGuardOpen,
    setNewPlanGuardOpen,
    savePlanForLeave,
    savingPlanForLeave,
    retryFetchStudies,
    studies,
    forms,
    selectedStudy,
    selectedForm,
    schema,
    selectedGroups,
    codebookDelta,
    codebookFilename,
    result,
    customInstructions,
    edits,
    suite,
    suiteRuns,
    saving,
    selectStudy,
    selectForm,
    toggleGroup,
    clearGroups: () => setSelectedGroups([]),
    uploadCodebook,
    generate,
    downloadExcel,
    saveRun,
    updateCustomInstructions,
    getEditedValue,
    setEditedValue,
    addTestCase,
    deleteTestCases,
    addStepToTestCase,
    deleteStepFromTestCase,
    clearCodebook: () => {
      setCodebookDelta(null);
      setCodebookFilename('');
    },
    loadingStudies,
    loadingForms,
    loadingSchema,
    generating,
    generationPhase,
    isImporting,
    parsingCodebook,
    error,
    // Val pipeline
    activeLayer,
    setActiveLayer,
    scenarioResults,
    generatingScenarios,
    scenarioProgress,
    scenarioStats,
    scenarioTokenCount,
    scenarioStreamText,
    generateScenarios,
    fetchStrideFormsOnly,
    scenarioReviews,
    setScenarioReview,
    savingScenarios,
    saveScenarioSuite,
    gapResults,
    generatingGaps,
    gapProgress,
    gapStats,
    gapTokenCount,
    gapStreamText,
    generateGaps,
    gapDispositions,
    setGapDisposition,
    savingGaps,
    saveGapSuite,
    appliedGaps,
    applyGapFix,
    strideMatchedStudy,
    setStrideMatchedStudy
  }), [codebookTree, codebookSource, codebookFiles, selectedCodebookFile, selectedCodebookPath, codebookSheets, selectedCodebookSheet, codebookType, setCodebookType, codebookSchema, codebookForms, selectedCodebookForms, loadingCodebookTree, loadingCodebookSheets, loadingCodebookSchema, selectCodebookFile, refreshCodebookTree, selectCodebookSheet, toggleCodebookForm, generateManual, downloadADO, importXlsx, clearSession, clearWork, hasPlanWork, isPlanComplete, isDirty, requestNewTestPlan, newPlanGuardOpen, savePlanForLeave, savingPlanForLeave, studies, forms, selectedStudy, selectedForm, schema, selectedGroups, codebookDelta, codebookFilename, result, customInstructions, edits, suite, suiteRuns, saving, selectStudy, selectForm, toggleGroup, uploadCodebook, generate, downloadExcel, saveRun, updateCustomInstructions, getEditedValue, setEditedValue, addTestCase, deleteTestCases, addStepToTestCase, deleteStepFromTestCase, loadingStudies, loadingForms, loadingSchema, generating, generationPhase, parsingCodebook, error, activeLayer, scenarioResults, generatingScenarios, scenarioProgress, scenarioStats, scenarioTokenCount, scenarioStreamText, generateScenarios, fetchStrideFormsOnly, scenarioReviews, setScenarioReview, savingScenarios, saveScenarioSuite, gapResults, generatingGaps, gapProgress, gapStats, gapTokenCount, gapStreamText, generateGaps, gapDispositions, setGapDisposition, savingGaps, saveGapSuite, appliedGaps, applyGapFix, strideMatchedStudy, isImporting, retryFetchStudies]);
  return <TestCaseContext.Provider value={value_3}>
      {children}
    </TestCaseContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useTestCases() {
  const ctx = useContext(TestCaseContext);
  if (!ctx) {
    throw new Error("useTestCases must be used within a TestCaseProvider");
  }
  return ctx;
}